import { useRef, useState } from "react";

import { IconProp } from "@fortawesome/fontawesome-svg-core";
import {
  faBriefcase,
  faBug,
  faBuilding,
  faCheck,
  faCloud,
  faCopy,
  faCube,
  faEye,
  faFlagAlt,
  faFolder,
  faKey,
  faPlug,
  faShield,
  faSparkles,
  faTableLayout,
  faTimesCircle,
  faUserCheck,
  faUserCircle,
  faUserEdit,
  faUserShield,
} from "@fortawesome/pro-regular-svg-icons";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useEventListener } from "usehooks-ts";
import { validate as isValidUUID } from "uuid";

import { useHistoryDecisionV3Basic } from "src/api/decisionHistoryV2/decisionHistoryQueries";
import { useFlows } from "src/api/queries";
import { useCaseRaw } from "src/cases/queries/cases";
import { useCaseQueues } from "src/cases/queries/queues";
import { useCaseTemplatesWithVersions } from "src/cases/queries/templates";
import { WorkspaceSimpleRoleType } from "src/clients/taktile-api";
import { useDecisionsOverview } from "src/decisionsOverview/queries";
import { invalidEntityIdsPattern } from "src/decisionsOverview/utils";
import { Icon } from "src/design-system/Icon";
import { toastActions } from "src/design-system/Toast/utils";
import { ExcludesFalse } from "src/flow/types";
import { useFolders } from "src/flowsOverview/v2/folderQueries";
import { useCapabilities } from "src/hooks/useCapabilities";
import {
  useRoleOverride,
  useRoleOverrideActions,
} from "src/hooks/useCapabilities/RoleOverrideStore";
import { getDowngradableRoles } from "src/hooks/useCapabilities/roleHierarchy";
import { useIsCaseManagementEnabled } from "src/hooks/useIsCaseManagementEnabled";
import { useIsIcebergEnabled } from "src/hooks/useIsIcebergEnabled";
import { useIsTaktileOrgUser } from "src/hooks/useIsTaktileOrgUser";
import { useJobsByRunId } from "src/jobs/api/queries";
import { ItemIcon } from "src/omnibox/ItemIcon";
import { MenuItem, OmniboxBase } from "src/omnibox/OmniboxBase";
import { OmniboxFeatureFlagLabel } from "src/omnibox/OmniboxFeatureFlagLabel";
import {
  useIsOmniboxVisible,
  useOmniboxActions,
} from "src/omnibox/OmniboxStore";
import { useWsInfo } from "src/powertools/useWsInfo";
import { getCloudFormationStackLink } from "src/powertools/utils";
import { FEATURE_FLAGS, isFeatureFlagEnabled } from "src/router/featureFlags";
import { useWorkspaceContext } from "src/router/routerContextHooks";
import {
  adminPanelPath,
  DashboardPageParams,
  getFlowVersionsUrl,
  getUrlToCaseQueuePage,
  getUrlToCaseTemplateVersionsPage,
  getUrlToCaseViewPage,
  getUrlToDecisionsOverview,
  getUrlToJobPage,
  getUrlToSettingsPage,
  getUrlToWsDashboard,
} from "src/router/urls";
import { SettingsSubpages } from "src/settings/SettingsSubpages";
import { useSimpleRole } from "src/store/SimpleRoleProvider";
import { isDevToolsAvailable } from "src/utils/environment";
import { logger } from "src/utils/logger";
import { useWorkspaceAndOrg } from "src/utils/useCurrentWorkspace";
import { useParamsDecode } from "src/utils/useParamsDecode";
import { uuidRegex } from "src/utils/uuidRegex";

enum ItemType {
  Action = "Action",
  FeatureFlag = "Feature Flag",
  DecisionFlow = "Decision Flow",
  Folder = "Folder",
  Workspace = "Workspace",
  Tool = "Tool",
  CaseQueue = "Case Queue",
  CaseTemplate = "Case Template",
}

type IdType =
  | "entity"
  | "decision"
  | "jobrun"
  | "case"
  | "copy-workspace-id"
  | "copy-org-id"
  | "debug-page"
  | "admin-panel"
  | "cloudformation";

type RoleOverrideId =
  | "role-viewer"
  | "role-reviewer"
  | "role-editor"
  | "role-admin"
  | "role-clear";

export const OmniboxOther = () => {
  const { workspace } = useWorkspaceContext();
  const isOmniboxVisible = useIsOmniboxVisible();
  const { showOmnibox, hideOmnibox } = useOmniboxActions();
  const [_, setSearchParams] = useSearchParams();
  const capabilities = useCapabilities();
  const [userInput, setUserInput] = useState<string>("");
  const { setOverrideRole, clearOverrideRole } = useRoleOverrideActions();
  const { overrideWorkspaceRole, overrideFWDOrAdmin } = useRoleOverride();
  const { workspaceRole: actualWorkspaceRole } = useSimpleRole();

  const wsInfo = useWsInfo(workspace);

  const isIcebergEnabled = useIsIcebergEnabled();
  const decisionSummaryQuery = useDecisionsOverview(
    workspace.base_url,
    {
      grouping_id: userInput,
      groupby: "flow.id",
      aggregate: "count",
    },
    {
      enabled: Boolean(userInput && isOmniboxVisible),
      icebergOverride: isIcebergEnabled,
    },
  );

  const decisionQuery = useHistoryDecisionV3Basic(
    {
      decisionId: userInput,
      baseUrl: workspace.base_url!,
    },
    {
      enabled: isValidUUID(userInput) && isOmniboxVisible,
    },
  );

  const jobsByRunIdQuery = useJobsByRunId(
    {
      baseURL: workspace.base_url!,
      runId: userInput,
    },
    {
      enabled: isValidUUID(userInput) && isOmniboxVisible,
    },
  );

  const { isEnabled: isCaseManagementEnabled } = useIsCaseManagementEnabled();

  const caseQuery = useCaseRaw(userInput, {
    enabled:
      isValidUUID(userInput) && isOmniboxVisible && isCaseManagementEnabled,
  });

  const searchMenuItemsVisibility = {
    isDecision:
      decisionQuery.isFetching ||
      (decisionQuery.isSuccess && !!decisionQuery.data),
    isEntity:
      decisionSummaryQuery.isFetching ||
      (decisionSummaryQuery.isSuccess &&
        decisionSummaryQuery.data.groups.length > 0),
    isJobRun:
      jobsByRunIdQuery.isFetching ||
      (jobsByRunIdQuery.isSuccess && jobsByRunIdQuery.data.jobs.length > 0),
    isCase: caseQuery.isFetching || (caseQuery.isSuccess && !!caseQuery.data),
  };

  const { orgs } = useWorkspaceAndOrg();
  const { orgId, wsId } = useParamsDecode<DashboardPageParams>();
  const isTaktileUser = useIsTaktileOrgUser();
  const isPowerToolsEnabled = isFeatureFlagEnabled(FEATURE_FLAGS.powertools);

  const document = useRef(window.document);
  const navigate = useNavigate();

  const handleClose = () => {
    hideOmnibox();
    setUserInput("");
  };

  const handleKeydown = (e: KeyboardEvent) => {
    if ((e.metaKey || e.ctrlKey) && e.code === "KeyK") {
      showOmnibox();
    }
    if (isOmniboxVisible && e.key === "Escape") {
      handleClose();
    }
  };
  useEventListener("keydown", handleKeydown, document);

  // Fetch the relevant data for the omnibox items: list of actions & feature flags
  const folders = useFolders(
    { workspaceId: wsId },
    { enabled: isOmniboxVisible },
  );
  const flows = useFlows({ workspaceId: wsId, disabled: !isOmniboxVisible });

  const { data: caseQueues } = useCaseQueues({
    enabled: isCaseManagementEnabled && isOmniboxVisible,
  });
  const { data: caseTemplates } = useCaseTemplatesWithVersions({
    enabled: isCaseManagementEnabled && isOmniboxVisible,
  });

  const actionOptions = [
    capabilities.connections.canAccess && {
      key: "connections",
      text: "Go to Connections",
      icon: faPlug,
    },
    capabilities.apiKeys.canAccess && {
      key: "api-keys",
      text: "Go to API keys",
      icon: faKey,
    },
    capabilities.usersPermissions.canAccess && {
      key: "users",
      text: "Go to Users & permissions",
      icon: faUserCircle,
    },
    isTaktileUser &&
      isFeatureFlagEnabled(FEATURE_FLAGS.powertools) && {
        key: "copy-workspace-id",
        text: "Copy workspace ID - wsId",
        icon: faCopy,
      },
    isTaktileUser &&
      isFeatureFlagEnabled(FEATURE_FLAGS.powertools) && {
        key: "copy-org-id",
        text: "Copy organization ID - orgId",
        icon: faCopy,
      },
    isTaktileUser &&
      isFeatureFlagEnabled(FEATURE_FLAGS.powertools) && {
        key: "debug-page",
        text: "Go to Debug page",
        icon: faBug,
      },
    isTaktileUser &&
      isFeatureFlagEnabled(FEATURE_FLAGS.powertools) && {
        key: "admin-panel",
        text: "Go to Admin Panel",
        icon: faShield,
      },
  ].filter(Boolean as unknown as ExcludesFalse);

  // Create omnibox items list for actions, feature flags and decision flows
  const folderItems: MenuItem[] =
    folders.data?.folders.map(({ name, id }) => ({
      label: name,
      icon: (
        <ItemIcon bgColor="bg-gray-200" variant="default" icon={faFolder} />
      ),
      type: "Folder",
      details: "",
      value: id,
      onSelect: () => selectItem(id, ItemType.Folder),
    })) ?? [];
  const decisionFlowItems: MenuItem[] =
    flows.data?.map(({ name, id, slug }) => ({
      label: isTaktileUser ? (
        <div className="flex">
          <p>{name}</p>
          <p className="ml-2 font-inter-normal-12px text-gray-500">{slug}</p>
        </div>
      ) : (
        name
      ),
      icon: (
        <ItemIcon
          bgColor="bg-indigo-200"
          variant="active"
          icon={faCube}
          padded
        />
      ),
      type: "Decision Flow",
      details: slug,
      value: id,
      onSelect: () => selectItem(id, ItemType.DecisionFlow),
    })) ?? [];
  const actionItems: MenuItem[] = actionOptions.map(({ key, icon, text }) => ({
    label: text,
    icon: (
      <ItemIcon bgColor="bg-gray-200" variant="default" icon={icon} padded />
    ),
    type: "Action",
    details: "",
    value: key,
    onSelect: () => selectItem(key, ItemType.Action),
  }));
  const featureFlagItems: MenuItem[] = Object.entries(FEATURE_FLAGS).map(
    ([key, value]) => ({
      label: <OmniboxFeatureFlagLabel featureFlag={value} />,
      icon: (
        <ItemIcon bgColor="bg-gray-200" variant="default" icon={faFlagAlt} />
      ),
      type: "Feature Flag",
      details: value,
      value: key,
      onSelect: () => selectItem(value, ItemType.FeatureFlag),
    }),
  );

  const caseQueueItems: MenuItem[] = isCaseManagementEnabled
    ? (caseQueues?.map((queue) => ({
        label: queue.name,
        icon: (
          <ItemIcon
            bgColor="bg-yellow-200"
            variant="warning"
            icon={faBriefcase}
            padded
          />
        ),
        type: "Case Queue",
        details: "",
        value: queue.id,
        onSelect: () => selectItem(queue.id, ItemType.CaseQueue),
      })) ?? [])
    : [];

  const caseTemplateItems: MenuItem[] = isCaseManagementEnabled
    ? (caseTemplates?.map((template) => ({
        label: template.attributes.template_name,
        icon: (
          <ItemIcon
            bgColor="bg-green-200"
            variant="success"
            icon={faTableLayout}
            padded
          />
        ),
        type: "Case Template",
        details: "",
        value: template.id,
        onSelect: () => selectItem(template.id, ItemType.CaseTemplate),
      })) ?? [])
    : [];

  const workspaceItems: MenuItem[] = orgs.flatMap((org) =>
    org.workspaces.map(({ name, id }) => ({
      label: `${org.name} - ${name}`,
      icon: (
        <ItemIcon bgColor="bg-gray-200" variant="default" icon={faBuilding} />
      ),
      type: "Workspace",
      details: "",
      value: `${org.id}:${id}`,
      onSelect: () => selectItem(`${org.id}:${id}`, ItemType.Workspace),
    })),
  );

  const cloudFormationItem: MenuItem = {
    label: "Go to Cloud Formation stack",
    icon: <ItemIcon bgColor="bg-gray-200" variant="default" icon={faCloud} />,
    type: "Action",
    details: "",
    value: "cloudformation",
    onSelect: () => selectItem("cloudformation", ItemType.Action),
  };

  const selectItem = (value: string, type: ItemType, query: string = "") => {
    try {
      switch (type) {
        case ItemType.FeatureFlag:
          setSearchParams((params) => {
            if (params.has(value)) {
              params.delete(value);
            } else {
              params.append(value, "true");
            }
            return params;
          });
          break;
        case ItemType.DecisionFlow:
          navigate(getFlowVersionsUrl(orgId, wsId, value));
          break;
        case ItemType.Tool:
          switch (value as RoleOverrideId) {
            case "role-viewer":
              setOverrideRole(
                WorkspaceSimpleRoleType.VIEWER,
                false,
                actualWorkspaceRole,
              );
              toastActions.success({
                title: "Role switched to Viewer",
              });
              break;
            case "role-reviewer":
              setOverrideRole(
                WorkspaceSimpleRoleType.REVIEWER,
                false,
                actualWorkspaceRole,
              );
              toastActions.success({
                title: "Role switched to Reviewer",
              });
              break;
            case "role-editor":
              setOverrideRole(
                WorkspaceSimpleRoleType.EDITOR,
                false,
                actualWorkspaceRole,
              );
              toastActions.success({
                title: "Role switched to Editor",
              });
              break;
            case "role-admin":
              setOverrideRole(
                WorkspaceSimpleRoleType.ADMIN,
                false,
                actualWorkspaceRole,
              );
              toastActions.success({
                title: "Role switched to Admin",
              });
              break;
            case "role-clear":
              clearOverrideRole();
              toastActions.success({
                title: "Role override cleared",
              });
              break;
          }
          break;
        case ItemType.Action:
          switch (value as IdType) {
            case "decision":
              if (query) {
                navigate(
                  getUrlToDecisionsOverview(orgId, wsId, { decisionId: query }),
                );
              }
              break;
            case "entity":
              if (query) {
                navigate(
                  getUrlToDecisionsOverview(orgId, wsId, { entityId: query }),
                );
              }
              break;
            case "jobrun":
              if (query && jobsByRunIdQuery.isSuccess) {
                const job = jobsByRunIdQuery.data.jobs[0];
                navigate(getUrlToJobPage(orgId, wsId, job.flow_id, job.id));
              }
              break;
            case "case":
              if (query) {
                navigate(getUrlToCaseViewPage(orgId, wsId, query));
              }
              break;
            case "copy-workspace-id":
              navigator.clipboard.writeText(wsId);
              toastActions.success({
                title: "Copied workspace ID to clipboard!",
              });
              break;
            case "copy-org-id":
              navigator.clipboard.writeText(orgId);
              toastActions.success({
                title: "Copied organization ID to clipboard!",
              });
              break;
            case "debug-page":
              navigate(
                getUrlToSettingsPage(orgId, wsId, SettingsSubpages.Powertools),
              );
              break;
            case "admin-panel":
              navigate(adminPanelPath);
              break;
            case "cloudformation":
              window.open(
                getCloudFormationStackLink(
                  wsInfo.data?.stack_name ?? "",
                  wsInfo.data?.region ?? "",
                ),
                "_blank",
              );
              break;
            default:
              navigate(
                getUrlToSettingsPage(orgId, wsId, value as SettingsSubpages),
              );
              break;
          }
          break;
        case ItemType.Folder:
          navigate(getUrlToWsDashboard({ orgId, wsId, folderId: value }));
          break;
        case ItemType.Workspace: {
          const [selectedOrgId, selectedWsId] = value.split(":");
          navigate(
            getUrlToWsDashboard({ orgId: selectedOrgId, wsId: selectedWsId }),
          );
          break;
        }
        case ItemType.CaseQueue:
          navigate(getUrlToCaseQueuePage({ orgId, wsId, queueId: value }));
          break;
        case ItemType.CaseTemplate:
          navigate(getUrlToCaseTemplateVersionsPage(orgId, wsId, value));
          break;
      }
    } catch (error) {
      logger.error("Error selecting item:", error);
    }
  };

  const entityItems = [
    searchMenuItemsVisibility.isEntity && {
      label: "Search for a Grouping ID",
      icon: (
        <ItemIcon
          bgColor="bg-gray-200"
          variant="default"
          icon={faSparkles}
          isLoading={decisionSummaryQuery.isFetching}
        />
      ),
      type: ItemType.Action,
      subType: "Grouping ID",
      details: "",
      value: "entity",
      onSelect: (query: string) => selectItem("entity", ItemType.Action, query),
      dataLoc: "omnibox-entity-search",
      pattern: invalidEntityIdsPattern,
      disabled: decisionSummaryQuery.isFetching,
    },
    searchMenuItemsVisibility.isDecision && {
      label: "Search for a Decision ID",
      icon: (
        <ItemIcon
          bgColor="bg-gray-200"
          variant="default"
          icon={faSparkles}
          isLoading={decisionQuery.isFetching}
        />
      ),
      type: ItemType.Action,
      subType: "Decision ID",
      details: "",
      value: "decision",
      onSelect: (query: string) =>
        selectItem("decision", ItemType.Action, query),
      dataLoc: "omnibox-decision-search",
      pattern: uuidRegex,
      disabled: decisionQuery.isFetching,
    },
    searchMenuItemsVisibility.isJobRun && {
      label: "Search for a Job run ID",
      icon: (
        <ItemIcon
          bgColor="bg-gray-200"
          variant="default"
          icon={faSparkles}
          isLoading={jobsByRunIdQuery.isFetching}
        />
      ),
      type: ItemType.Action,
      subType: "Job run ID",
      details: "",
      value: "jobrun",
      onSelect: (query: string) => selectItem("jobrun", ItemType.Action, query),
      pattern: uuidRegex,
      disabled: jobsByRunIdQuery.isFetching,
    },
    searchMenuItemsVisibility.isCase && {
      label: "Search for a Case ID",
      icon: (
        <ItemIcon
          bgColor="bg-gray-200"
          variant="default"
          icon={faSparkles}
          isLoading={caseQuery.isFetching}
        />
      ),
      type: ItemType.Action,
      subType: "Case ID",
      details: "",
      value: "case",
      onSelect: (query: string) => selectItem("case", ItemType.Action, query),
      dataLoc: "omnibox-case-search",
      pattern: uuidRegex,
      disabled: caseQuery.isFetching,
    },
  ].filter(Boolean as unknown as ExcludesFalse);

  // Helper to check if current role matches
  const isCurrentRole = (
    role: WorkspaceSimpleRoleType,
    isFWD: boolean,
  ): boolean => {
    if (overrideWorkspaceRole === null) return false;
    return overrideWorkspaceRole === role && overrideFWDOrAdmin === isFWD;
  };

  // Get roles that user can downgrade to (for security)
  const downgradableRoles = actualWorkspaceRole
    ? getDowngradableRoles(actualWorkspaceRole)
    : [];

  // Only allow role override on dev environment
  const isDevEnvironment = isDevToolsAvailable();

  // Role menu items configuration
  const allRoleItems: Array<{
    role: WorkspaceSimpleRoleType;
    id: RoleOverrideId;
    label: string;
    icon: IconProp;
    details: string;
    dataLoc: string;
  }> = [
    {
      role: WorkspaceSimpleRoleType.VIEWER,
      id: "role-viewer",
      label: "Switch to Viewer",
      icon: faEye,
      details: "switch role viewer permissions read-only view",
      dataLoc: "omnibox-role-viewer",
    },
    {
      role: WorkspaceSimpleRoleType.REVIEWER,
      id: "role-reviewer",
      label: "Switch to Reviewer",
      icon: faUserCheck,
      details: "switch role reviewer permissions review cases",
      dataLoc: "omnibox-role-reviewer",
    },
    {
      role: WorkspaceSimpleRoleType.EDITOR,
      id: "role-editor",
      label: "Switch to Editor",
      icon: faUserEdit,
      details: "switch role editor permissions edit modify",
      dataLoc: "omnibox-role-editor",
    },
    {
      role: WorkspaceSimpleRoleType.ADMIN,
      id: "role-admin",
      label: "Switch to Admin",
      icon: faUserShield,
      details: "switch role admin administrator permissions full-access",
      dataLoc: "omnibox-role-admin",
    },
  ];

  const toolItems: MenuItem[] = [
    // Only show role override items on dev environment
    ...(isDevEnvironment
      ? [
          // Only show roles that are downgrades from current role
          ...allRoleItems
            .filter((item) => downgradableRoles.includes(item.role))
            .map((item) => ({
              label: (
                <div className="flex items-center gap-2">
                  <span>{item.label}</span>
                  {isCurrentRole(item.role, false) && (
                    <Icon icon={faCheck} size="xs" variant="success" />
                  )}
                </div>
              ),
              icon: (
                <ItemIcon
                  bgColor="bg-yellow-200"
                  variant="warning"
                  icon={item.icon}
                />
              ),
              type: ItemType.Tool,
              details: item.details,
              value: item.id,
              onSelect: () => selectItem(item.id, ItemType.Tool),
              dataLoc: item.dataLoc,
            })),
          ...(overrideWorkspaceRole !== null
            ? [
                {
                  label: "Clear role override",
                  icon: (
                    <ItemIcon
                      bgColor="bg-red-200"
                      variant="error"
                      icon={faTimesCircle}
                    />
                  ),
                  type: ItemType.Tool,
                  details: "clear remove reset role override restore original",
                  value: "role-clear",
                  onSelect: () => selectItem("role-clear", ItemType.Tool),
                  dataLoc: "omnibox-role-clear",
                },
              ]
            : []),
        ]
      : []),
  ];

  const menuItems = isTaktileUser
    ? [
        ...folderItems,
        ...decisionFlowItems,
        ...caseQueueItems,
        ...caseTemplateItems,
        ...actionItems,
        ...entityItems,
        ...featureFlagItems,
        ...(isPowerToolsEnabled ? [...workspaceItems, cloudFormationItem] : []),
        ...toolItems,
      ]
    : [...folderItems, ...decisionFlowItems, ...actionItems, ...entityItems];

  const currentUrl = window.location.href;
  const versionRegex = /\/version\//;
  const isVersionPage = versionRegex.test(currentUrl);

  return (
    !isVersionPage && (
      <OmniboxBase
        data-loc="omnibox-canvas"
        menuItems={menuItems}
        open={isOmniboxVisible}
        placeholder="Search for a Decision ID, Grouping ID, Decision Flows, or actions"
        onClose={handleClose}
        onQuery={setUserInput}
      />
    )
  );
};
